// -----------------------------------------------------------------------------
// Message composer — a mention-aware contenteditable editor (see
// `ChatComposerInput`) with auto-grow, Enter-to-send and Shift+Enter for a
// newline. Resets to empty whenever the active channel changes so half-typed
// drafts don't leak between conversations. The serialized body it emits carries
// inline @-mention tokens for users / parts / vehicles / orders / leads.
//
// Files can be attached via the paperclip button, pasted from the
// clipboard (screenshots), or dragged and dropped onto the composer —
// images are compressed client-side before upload, mirroring the shared
// FileUpload component. Attachment-only messages are allowed — the body
// may be empty when at least one file is staged.
//
// When `replyTo` is set the composer shows a "replying to" banner and the
// send carries the original message's id.
// -----------------------------------------------------------------------------

import { useEffect, useRef, useState } from "react";
import {
  ArrowUturnLeftIcon,
  DocumentIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Compressor from "compressorjs";

import classNames from "../../utils/classNames";
import { fullNameFor } from "../../utils/chatHelpers";
import { IChatChannel, IChatMessage, IChatUser } from "../../models/Chat";
import ChatComposerInput, {
  ChatComposerInputHandle,
} from "../../components/chat/ChatComposerInput";

// Matches the server-side allow-list in ChatService.
const ACCEPTED_FILE_TYPES = ".jpg,.jpeg,.png,.mp4,.pdf";
const ACCEPTED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".mp4", ".pdf"];

const isAcceptedFile = (file: File) => {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const compressImage = (file: File) =>
  new Promise<File>((resolve) => {
    new Compressor(file, {
      quality: 0.7,
      success(blob) {
        resolve(new File([blob], file.name, { type: file.type }));
      },
      error() {
        // Compression is best-effort — fall back to the original file.
        resolve(file);
      },
    });
  });

interface ComposerProps {
  channel: IChatChannel;
  usersById: Map<string, IChatUser>;
  meId: string;
  sending?: boolean;
  /** Message being replied to - shows the banner and rides on the send. */
  replyTo?: IChatMessage | null;
  onCancelReply?: () => void;
  /** Called on every keystroke - the parent throttles the hub invoke. */
  onTyping?: () => void;
  onSend: (body: string, files: File[]) => void;
}

export default function Composer({
  channel,
  usersById,
  meId,
  sending = false,
  replyTo = null,
  onCancelReply,
  onTyping,
  onSend,
}: ComposerProps) {
  const [value, setValue] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<ChatComposerInputHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Drop staged files when switching conversations, same as the text draft.
  useEffect(() => {
    setPendingFiles([]);
  }, [channel.id]);

  // Focus the editor when a reply target is picked so the user can type
  // straight away.
  useEffect(() => {
    if (replyTo) inputRef.current?.focus();
  }, [replyTo]);

  const canSend = (value.trim().length > 0 || pendingFiles.length > 0) && !sending;

  const handleSubmit = () => {
    const body = (inputRef.current?.getValue() ?? "").trim();
    if (!body && pendingFiles.length === 0) return;
    if (sending) return;
    onSend(body, pendingFiles);
    inputRef.current?.clear();
    setValue("");
    setPendingFiles([]);
  };

  const handleChange = (serialized: string) => {
    setValue(serialized);
    onTyping?.();
  };

  const stageFiles = async (files: File[]) => {
    const accepted = files.filter(isAcceptedFile);
    if (accepted.length === 0) return;
    const processed = await Promise.all(
      accepted.map((f) =>
        f.type.startsWith("image/") ? compressImage(f) : Promise.resolve(f)
      )
    );
    setPendingFiles((prev) => [...prev, ...processed]);
  };

  const handleFilesPicked = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    await stageFiles(Array.from(fileList));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Screenshots and copied images arrive via the clipboard's file list.
  // Text pastes have no files and fall through to the editor untouched.
  const handlePaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData?.files ?? []);
    if (files.length === 0) return;
    e.preventDefault();
    stageFiles(files);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length > 0) stageFiles(files);
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const placeholder =
    channel.kind === "channel"
      ? `Message #${channel.name}`
      : `Send a direct message`;

  const replyAuthor = replyTo ? usersById.get(replyTo.authorId) : undefined;

  return (
    <div className="border-t border-gray-200 bg-white p-3">
      <div
        onPaste={handlePaste}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={classNames(
          "rounded-lg border shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500",
          dragging
            ? "border-primary-400 ring-1 ring-primary-400"
            : "border-gray-300"
        )}
      >
        {replyTo ? (
          <div className="flex items-start gap-2 border-b border-gray-100 px-3 pt-2 pb-1.5">
            <ArrowUturnLeftIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-500" />
            <div className="min-w-0 flex-1 text-xs">
              <span className="font-semibold text-gray-700">
                Replying to {replyAuthor ? fullNameFor(replyAuthor) : "Unknown"}
              </span>
              <p className="line-clamp-1 break-words text-gray-500">
                {replyTo.body ||
                  (replyTo.attachments?.length ? "📎 Attachment" : "")}
              </p>
            </div>
            <button
              type="button"
              onClick={onCancelReply}
              title="Cancel reply"
              className="rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
        {pendingFiles.length > 0 ? (
          <div className="flex flex-wrap gap-2 border-b border-gray-100 px-3 pt-2.5 pb-2">
            {pendingFiles.map((file, index) => (
              <PendingAttachment
                key={`${file.name}-${index}`}
                file={file}
                onRemove={() => removePendingFile(index)}
              />
            ))}
          </div>
        ) : null}
        <ChatComposerInput
          ref={inputRef}
          resetKey={channel.id}
          placeholder={placeholder}
          usersById={usersById}
          meId={meId}
          onChange={handleChange}
          onSubmit={handleSubmit}
          className="px-3 py-2.5"
        />
        <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-2 py-1.5">
          <div className="flex min-w-0 items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_FILE_TYPES}
              className="hidden"
              onChange={(e) => handleFilesPicked(e.target.files)}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              title="Attach files"
              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <PaperClipIcon className="h-4 w-4" />
            </button>
            <p className="truncate text-[10px] text-gray-400">
              <kbd className="rounded border border-gray-200 bg-gray-50 px-1">
                @
              </kbd>{" "}
              to mention,{" "}
              <kbd className="rounded border border-gray-200 bg-gray-50 px-1">
                Enter
              </kbd>{" "}
              to send,{" "}
              <kbd className="rounded border border-gray-200 bg-gray-50 px-1">
                Shift+Enter
              </kbd>{" "}
              for a new line
            </p>
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSend}
            className={classNames(
              "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold",
              canSend
                ? "bg-primary-600 text-white hover:bg-primary-500"
                : "bg-gray-100 text-gray-400"
            )}
          >
            <PaperAirplaneIcon className="h-3.5 w-3.5" />
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Staged-file chip — image files get an inline object-URL preview, other
// types show a generic document glyph. The object URL is revoked on
// unmount so removed/sent files don't leak memory.
function PendingAttachment({
  file,
  onRemove,
}: {
  file: File;
  onRemove: () => void;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div className="group relative flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 py-1 pl-1 pr-2">
      {previewUrl ? (
        <img
          src={previewUrl}
          alt={file.name}
          className="h-9 w-9 rounded object-cover"
        />
      ) : (
        <span className="flex h-9 w-9 items-center justify-center rounded bg-white text-gray-400">
          <DocumentIcon className="h-5 w-5" />
        </span>
      )}
      <span className="max-w-[10rem] truncate text-xs text-gray-600">
        {file.name}
      </span>
      <button
        type="button"
        onClick={onRemove}
        title="Remove attachment"
        className="absolute -right-1.5 -top-1.5 rounded-full border border-gray-200 bg-white p-0.5 text-gray-400 shadow-sm hover:text-gray-600"
      >
        <XMarkIcon className="h-3 w-3" />
      </button>
    </div>
  );
}
